﻿// General mixin for shared functionality among Vue instances
const mixin = {
	methods: {
		// Remove last three zeroes from a dossierNumber.
		parseDossierNr(value) {
			if (!value) return '';

			let formattedValue = value.toString();

			if (formattedValue.slice(-3) === '000') {
				formattedValue = formattedValue.slice(0, -3);
			}

			return formattedValue.charAt(0).toUpperCase() + formattedValue.slice(1);
		},
		// Simple capitalize function.
		capitalize(value) {
			if (!value) return '';
			value = value.toString();
			return value.charAt(0).toUpperCase() + value.slice(1);
		},
		// Simple whitespace strip function.
		strip(value) {
			if (!value) return '';
			value = value.toString();
			return value.replace(/\s/g, '');
		},
		// Simple date object handler.
		toDateObj(dateString) {
			return new Date(dateString);
		},
		// Simple date string handler. (DD-MM-YYYY)
		toDateStr(dateString) {
			if (!dateString) return null;
			return new Date(dateString).toLocaleDateString('nl-NL');
		},
		// Simple date string handler with full month. (DD-[fullmonth]-YYYY)
		toDateStrFullMnth(dateString) {
			if (!dateString) return null;

			const monthsInDutch = [
				'januari',
				'februari',
				'maart',
				'april',
				'mei',
				'juni',
				'juli',
				'augustus',
				'september',
				'oktober',
				'november',
				'december',
			];

			const date = new Date(dateString);
			const day = date.getDate();
			const month = monthsInDutch[date.getMonth()];
			const year = date.getFullYear();

			return `${day}-${month}-${year}`;
		},
		// Simple date string handler with full month. (DD-[shortmonth]-YYYY)
		toDateStrShortMnth(dateString) {
			if (!dateString) return null;

			const monthsInDutch = [
				'jan',
				'feb',
				'mrt',
				'apr',
				'mei',
				'jun',
				'jul',
				'aug',
				'sep',
				'okt',
				'nov',
				'dec',
			];

			const date = new Date(dateString);
			const day = date.getDate();
			const month = monthsInDutch[date.getMonth()];
			const year = date.getFullYear();

			return `${day}-${month}-${year}`;
		},
		// Simple array reverser.
		reverse(array) {
			return [...array].reverse();
		},
		// Simple array duplicate remover.
		removeDuplicates(array) {
			return [...new Set(array)];
		},
		// General debounce function for reducing unnecessary API calls.
		debounce(cb, delay = 500) {
			let timeout;
			return (...args) => {
				clearTimeout(timeout);
				timeout = setTimeout(() => {
					cb(...args);
				}, delay);
			};
		},
		// Function to output currerncy in euros, Dutch style
		toEuro(number, currencySign = false) {
			if (number === null) return '---';
			if (isNaN(parseInt(number))) return null;
			let amount = Number(number).toLocaleString('nl-NL', {
				style: 'currency',
				currency: 'EUR',
			});
			if (currencySign) {
				return amount
			} else {
				return amount.replace('€', '').trim()
			}
		},
		// Function to output percentages
		toPercentage(number) {
			if (isNaN(parseInt(number))) return null;
			return Math.round(Number(number) * 100) + '%';
		},
		// Function to sort array by key
		sortByKey(arr, key) {
			if (!arr || !arr.length) return [];
			return arr.sort((a, b) => a[key] - b[key]);
		},
		// Function to sort dossier objects by month and then by year
		sortDossiersByDate(dossiers) {
			if (!dossiers || !dossiers.length) return [];
			let sortedDossiers = [...dossiers];

			return sortedDossiers
				.sort((a, b) => parseInt(a.ingangsjaarmaand?.split('-')[1] ?? []) - parseInt(b.ingangsjaarmaand?.split('-')[1] ?? []))
				.sort((a, b) => parseInt(a.ingangsjaarmaand?.split('-')[0] ?? []) - parseInt(b.ingangsjaarmaand?.split('-')[0] ?? []));
		},
		sortDossiersByEndDate(dossiers) {
			if (!dossiers || !dossiers.length) return [];
			let sortedDossiers = [...dossiers];

			return sortedDossiers
				.sort((a, b) => parseInt(a.afsluitjaarmaand?.split('-')[1] ?? []) - parseInt(b.afsluitjaarmaand?.split('-')[1] ?? []))
				.sort((a, b) => parseInt(a.afsluitjaarmaand?.split('-')[0] ?? []) - parseInt(b.afsluitjaarmaand?.split('-')[0] ?? []));
		},
		sortDossiersByStatusDate(dossiers) {
			if (!dossiers || !dossiers.length) return [];
			let sortedDossiers = [...dossiers];
			return sortedDossiers.sort(
				(a, b) => new Date(b.status_datum) - new Date(a.status_datum)
			);
		},
		// Function to filter array by key-value pair
		filterByKeyValue(arr, key, value) {
			if (!arr || !arr.length) return [];
			return arr.filter((x) => x[key] == value);
		},
		// Function to filter dossiers by filters with the option of exclusions
		filterDossiers(dossiers, filters, excludedFilters = []) {
			if (!dossiers || !dossiers.length) return [];
			if (!filters || !filters.length) return dossiers;
			let filteredDossiers = [...dossiers];
			filters.forEach((filter) => {
				if (!excludedFilters.includes(filter.type)) {
					switch (filter.type) {
						case 'reference':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.referentie_nr)
							);
							break;
						case 'clientName':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.opdrachtgever_naam)
							);
							break;
						case 'clientId':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.opdrachtgever_portal_relatie_nr)
							);
							break;
						case 'creditorName':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.eiser_portal_naam)
							);
							break;
						case 'creditorId':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.eiser_portal_relatie_nr)
							);
							break;
						case 'status':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								 dossier.lopend_afgesloten === filter.value
							);
							break;
						case 'rentalDataStatus':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								 dossier.status_huurcontract === filter.value
							);
							break;
						case 'waitingStatus':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								dossier.status_wachtreactie_jn_label === filter.value
							);
							break;
						case 'period':
							if (typeof filter.value === 'number') {
								filteredDossiers = filteredDossiers.filter(
									(dossier) => parseInt(dossier.ingangsjaar) === filter.value
								);
							} else {
								let mostRecentYear = parseInt(
									dossiers[dossiers.length - 1].ingangsjaar
								);
								let secondRecentYear = mostRecentYear
									? mostRecentYear - 1
									: null;

								filteredDossiers = filteredDossiers.filter(
									(dossier) =>
										parseInt(dossier.ingangsjaar) !== mostRecentYear &&
										parseInt(dossier.ingangsjaar) !== secondRecentYear
								);
							}
							break;
						case 'periodEnd':
							if (typeof filter.value === 'number') {
								filteredDossiers = filteredDossiers.filter(
									(dossier) => parseInt(dossier.afsluitjaar) === filter.value
								);
							} else {
								let mostRecentYear = parseInt(
									dossiers[dossiers.length - 1].afsluitjaar
								);
								let secondRecentYear = mostRecentYear
									? mostRecentYear - 1
									: null;

								filteredDossiers = filteredDossiers.filter(
									(dossier) =>
										parseInt(dossier.afsluitjaar) !== mostRecentYear &&
										parseInt(dossier.afsluitjaar) !== secondRecentYear
								);
							}
							break;
						case 'phase':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								dossier.fase_label === filter.value
							);
							break;
						case 'closingReason':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								parseInt(dossier.afsluitreden_groep_code) === filter.value
							);
							break;
						case 'detailedStatus':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								dossier.status_extern_label === filter.value
							);
							break;
						case 'detailedClosingReason':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								dossier.afsluit_oms === filter.value
							);
							break;
						case 'kind':
							if (filter.value === 'Schuldbewaking') {
								filteredDossiers = filteredDossiers.filter(
									(dossier) => dossier.dossier_soort_ogp_code === 'BWK'
								);
							} else {
								filteredDossiers = filteredDossiers.filter(
									(dossier) => dossier.dossier_soort_ogp_code !== 'BWK'
								);
							}
							break;
						case 'dossierId':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.dossier_nr)
							);
							break;
						case 'debtorName':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.debiteur_naam)
							);
							break;
						case 'clientNameId':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(`${dossier.opdrachtgever_portal_relatie_nr} - ${dossier.opdrachtgever_portal_naam}`)
							);
							break;
						case 'creditorNameId':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(`${dossier.eiser_portal_relatie_nr} - ${dossier.eiser_portal_naam}`)
							);
							break;
						case 'statusExternal':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.status_extern_label)
							);
							break;
						case 'settlingReason':
							filteredDossiers = filteredDossiers.filter((dossier) =>
								filter.value.includes(dossier.afsluitreden_groep_label)
							);
							break;
						default: {
							break;
						}
					}
				}
			});
			return filteredDossiers;
		},
		// Function to sort dashboards phases
		sortPhases(labels) {
			if (!labels || !labels.length) return [];
			let phases = [...labels];
			return phases
				.map((phase) => {
					let order;
					let backgroundColor;
					switch (phase) {
						case 'Minnelijk':
							order = 1;
							backgroundColor = '#f29800';
							break;
						case 'Procedure':
							order = 2;
							backgroundColor = '#1dab9e';
							break;
						case 'Executie':
							order = 3;
							backgroundColor = '#4e92bc';
							break;
						default:
							order = 4;
							backgroundColor = '#36a2eb';
							break;
					}
					return {
						label: phase,
						order: order,
						backgroundColor: backgroundColor,
					};
				})
				.sort((a, b) => a.order - b.order);
		},
		// Function to check if a value is truthy
		checkValue(value) {
			if (value) return value;
			return '-';
		},
		// Function to sort an array regardless of alphanumerical values
		sortArray(array) {
			if (!array || !array.length) return [];
			return array.sort((a, b) => {
				// Convert both to strings for alphabetical comparison
				if (typeof a === "string" && typeof b === "string") {
					return a.localeCompare(b);
				}
				// Handle numeric sorting
				else if (typeof a === "number" && typeof b === "number") {
					return a - b;
				}
				// Sort numbers before strings if mixed
				else if (typeof a === "number") {
					return -1;
				} else {
					return 1;
				}
			})
		},
		// Function to sort an array of year-month strings
		sortYearMonth(array) {
            if (!array || !array.length) return [];
            return array.sort((a, b) => {
				const [yearA, monthA] = a.split('-').map(Number);
				const [yearB, monthB] = b.split('-').map(Number);

				if (yearA !== yearB) {
					return yearA - yearB;
				} else {
					return monthA - monthB;
				}
            });
        },
		// Function to convert a date to a universal date without time issues
		toUniversalDate(date) {
			if (!date) return null;
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return new Date(`${year}-${month}-${day}`);
		},
		// Function to convert a filter alias string to a filter object for filtering through API
		transformFilterItem(filterItem) {
			if (!filterItem || !filterItem.type || !filterItem.value) return null;
			switch (filterItem.type) {
				case 'waitingStatus':
					return {
						Operator: 'Eq',
						Key: 'status_wachtreactie_jn_label',
						Values: [filterItem.value],
					};
					break;
				case 'reference':
					return {
						Operator: 'In',
						Key: 'referentie_nr',
						Values: filterItem.value,
					};
					break;
				case 'clientName':
					return {
                        Operator: 'In',
                        Key: 'opdrachtgever_naam',
                        Values: filterItem.value,
                    };
					break;
				case 'clientId':
					return {
                        Operator: 'In',
                        Key: 'opdrachtgever_portal_relatie_nr',
                        Values: filterItem.value,
					};
					break;
                case 'creditorName':
                    return {
                        Operator: 'In',
                        Key: 'eiser_portal_naam',
                        Values: filterItem.value,
                    };
					break;
				case 'creditorId':
					return {
                        Operator: 'In',
                        Key: 'eiser_portal_relatie_nr',
                        Values: filterItem.value,
                    };
					break;
				case 'closingReason':
					return {
                        Operator: 'Eq',
                        Key: 'afsluitreden_groep_code',
						Values: [filterItem.value],
                    };
					break;
				case 'phase':
					return {
                        Operator: 'Eq',
                        Key: 'fase_label',
                        Values: [filterItem.value],
					};
					break;
				case 'period':
					if (typeof filterItem.value === 'number') {
						return {
							Operator: 'Eq',
							Key: 'ingangsjaar',
							Values: [filterItem.value.toString()],
						};
					}
					else {
						return {
							Operator: 'NotEq',
							Key: 'ingangsjaar',
							Values: [filterItem.mostRecentYear.toString(), filterItem.secondRecentYear.toString()],
                        };
					}
					break;
				case 'periodEnd':
					if (typeof filterItem.value === 'number') {
						return {
							Operator: 'Eq',
							Key: 'afsluitjaar',
							Values: [filterItem.value.toString()],
						};
					}
					else {
						return {
							Operator: 'NotEq',
							Key: 'afsluitjaar',
							Values: [filterItem.mostRecentYear.toString(), filterItem.secondRecentYear.toString()],
						};
					}
					break;
				case 'status':
					return {
						Operator: 'Eq',
						Key: 'lopend_afgesloten',
						Values: [filterItem.value],
					};
					break;
				case 'kind':
					if (filterItem.value === 'Schuldbewaking') {
						return {
							Operator: 'Eq',
							Key: 'dossier_soort_ogp_code',
							Values: ['BWK'],
						};
					} else {
						return {
							Operator: 'NotEq',
							Key: 'dossier_soort_ogp_code',
							Values: ['BWK'],
						};
					}
					break;
				case 'detailedStatus':
					return {
						Operator: 'Eq',
						Key: 'status_extern_label',
						Values: [filterItem.value],
					};
					break;
				case 'detailedClosingReason':
					return {
						Operator: 'Eq',
						Key: 'afsluit_oms',
						Values: [filterItem.value],
					};
					break;
				case 'rentalDataStatus':
					return {
                        Operator: 'Eq',
                        Key: 'status_huurcontract',
                        Values: [filterItem.value],
                    };
                    break;
				default:
					return null;
			}
		},
	}
};

export default mixin;
